import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import './timerPage.css';

export default function TimerPage() {
  const [searchParams] = useSearchParams();
  const workTime = parseInt(searchParams.get('work'), 10);
  const restTime = parseInt(searchParams.get('rest'), 10);
  const totalSets = parseInt(searchParams.get('sets'), 10);

  const [mode, setMode] = useState(null);
  const [timeLeft, setTimeLeft] = useState(workTime);
  const [setCount, setSetCount] = useState(1);
  const [done, setDone] = useState(false);
  const [countInTime, setCountInTime] = useState(3);

  const navigate = useNavigate();
  const countInIntervalRef = useRef(null);
  const timerIntervalRef = useRef(null);
  const audioCtxRef = useRef(null);

  // 🟢 Wake lock reference
  const wakeLockRef = useRef(null);

  // 🟢 Request wake lock when timer starts
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator && navigator.wakeLock.request) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        console.log('✅ Wake lock active');
        wakeLockRef.current.addEventListener('release', () => {
          console.log('⚠️ Wake lock released');
        });
      } else {
        console.warn('Wake Lock API not supported on this device');
      }
    } catch (err) {
      console.error('Wake lock error:', err);
    }
  };

  // 🟢 Release wake lock when done or leaving
  const releaseWakeLock = async () => {
    try {
      await wakeLockRef.current?.release();
      wakeLockRef.current = null;
      console.log('🛑 Wake lock released manually');
    } catch (err) {
      console.error(err);
    }
  };

  const handleStop = async () => {
    clearInterval(countInIntervalRef.current);
    clearInterval(timerIntervalRef.current);
    await releaseWakeLock(); // release screen lock
    setMode('Done');
    setDone(true);
    navigate('/');
  };

  const handleStart = async () => {
    // iPhone audio resume fix
    if (!audioCtxRef.current || audioCtxRef.current.state === 'closed') {
      audioCtxRef.current = new (window.AudioContext ||
        window.webkitAudioContext)();
    } else if (audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume();
    }

    // 🟢 Request wake lock now
    await requestWakeLock();

    beepIt('high');
    setTimeout(() => {
      setMode('CountIn');
      setCountInTime(3);
    }, 1000);
  };

  useEffect(() => {
    audioCtxRef.current = new (window.AudioContext ||
      window.webkitAudioContext)();

    // 🟢 Release wake lock if user leaves the page
    return () => {
      releaseWakeLock();
    };
  }, []);

  const beepIt = (field) => {
    const context = audioCtxRef.current;
    if (!context) return;

    if (context.state === 'suspended') {
      context.resume();
    }

    const freq = field === 'high' ? 1700 : 700;
    const duration = field === 'high' ? 0.2 : 0.6;

    const oscillator = context.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, context.currentTime);
    oscillator.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + duration);
  };

  // Count-in
  useEffect(() => {
    if (mode !== 'CountIn') return;
    beepIt('high');
    countInIntervalRef.current = setInterval(() => {
      if (countInTime > 1) {
        setCountInTime((prev) => prev - 1);
      } else {
        beepIt('low');
        clearInterval(countInIntervalRef.current);
        setTimeout(() => {
          setMode('Work');
          setTimeLeft(workTime);
        }, 700);
      }
    }, 1000);

    return () => clearInterval(countInIntervalRef.current);
  }, [countInTime, mode, workTime]);

  // Timer
  useEffect(() => {
    if (mode === 'CountIn' || mode === 'Done' || mode === null) return;

    timerIntervalRef.current = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;

        if (next <= 3 && next > 0) beepIt('high');
        if (next === 0) beepIt('low');

        if (next < 0) {
          clearInterval(timerIntervalRef.current);

          if (mode === 'Work') {
            setMode('Rest');
            setTimeLeft(restTime);
          } else {
            const nextSet = setCount + 1;
            if (nextSet <= totalSets) {
              setSetCount(nextSet);
              setMode('Work');
              setTimeLeft(workTime);
            } else {
              setMode('Done');
              setTimeLeft(0);
              setDone(true);
              releaseWakeLock(); // 🟢 release at end
            }
          }
        }

        return next;
      });
    }, 1000);

    return () => clearInterval(timerIntervalRef.current);
  }, [timeLeft, mode, restTime, setCount, totalSets, workTime]);

  return (
    <div className='timer-container'>
      <div className='timer-card' style={{ padding: 20 }}>
        {!done && (
          <button onClick={handleStop} className='timer-button-stop'>
            Stop
          </button>
        )}

        {mode === 'CountIn' ? (
          <>
            <h2 className='timer-label'>Get Ready</h2>
            <h1 className='timer-display'>{countInTime}</h1>
          </>
        ) : (
          <>
            <h2 className='timer-label'>
              Set {setCount} / {totalSets}
            </h2>
            <h1 className='timer-mode'>{mode}</h1>
            <h1 className='timer-display'>{timeLeft > 0 ? timeLeft : 0}</h1>
          </>
        )}

        {mode === null && (
          <button onClick={handleStart} className='start-button'>
            Start
          </button>
        )}
      </div>
    </div>
  );
}
